import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import '../styles/AdminMessaging.css';

const AdminMessaging = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [isCritical, setIsCritical] = useState(false);
  const [targetType, setTargetType] = useState('all'); // 'all', 'roles', 'users'
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedRoleDropdown, setSelectedRoleDropdown] = useState('');
  const [selectedUserDropdown, setSelectedUserDropdown] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [retentionDays, setRetentionDays] = useState(30);
  const [archiveEnabled, setArchiveEnabled] = useState(false);
  const [archiveBucket, setArchiveBucket] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  useEffect(() => {
    // Fetch available roles and users
    fetchRolesAndUsers();
    fetchMessagingSettings();
  }, []);

  const fetchRolesAndUsers = async () => {
    try {
      setIsLoadingData(true);
      
      // Fetch roles from the server
      const rolesResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/roles`
      );
      
      // Extract unique role names from the roles data
      const roleNames = rolesResponse.data.map(role => role.name);
      setAvailableRoles(roleNames);

      // Fetch users from the server
      const usersResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/users`
      );
      
      // Filter only active users and format the data
      const activeUsers = usersResponse.data
        .filter(user => user.active)
        .map(user => ({
          id: user.id,
          name: user.name,
          role: user.role,
          email: user.email
        }));
      
      setAvailableUsers(activeUsers);
    } catch (error) {
      console.error('Error fetching roles and users:', error);
      // Fallback to basic roles if API fails
      setAvailableRoles(['admin', 'doctor', 'nurse', 'receptionist', 'patient']);
      setAvailableUsers([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddRole = () => {
    if (selectedRoleDropdown && !selectedRoles.includes(selectedRoleDropdown)) {
      setSelectedRoles(prev => [...prev, selectedRoleDropdown]);
      setSelectedRoleDropdown('');
    }
  };

  const handleRemoveRole = (role) => {
    setSelectedRoles(prev => prev.filter(r => r !== role));
  };

  const handleAddUser = () => {
    const userIdToAdd = parseInt(selectedUserDropdown);
    console.log('Adding user:', selectedUserDropdown, 'parsed as:', userIdToAdd);
    console.log('Current selectedUsers:', selectedUsers);
    console.log('Already includes user?', selectedUsers.includes(userIdToAdd));
    
    if (selectedUserDropdown && !selectedUsers.includes(userIdToAdd)) {
      setSelectedUsers(prev => [...prev, userIdToAdd]);
      setSelectedUserDropdown('');
    }
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(prev => prev.filter(id => id !== userId));
  };

  const fetchMessagingSettings = async () => {
    try {
      setIsLoadingSettings(true);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admin/messaging-settings`
      );
      setRetentionDays(response.data.retention_days ?? 30);
      setArchiveEnabled(!!response.data.archive_enabled);
      setArchiveBucket(response.data.archive_bucket || '');
    } catch (error) {
      console.error('Error fetching messaging settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setIsSavingSettings(true);
      await axios.put(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admin/messaging-settings`,
        {
          retention_days: parseInt(retentionDays, 10),
          archive_enabled: archiveEnabled,
          archive_bucket: archiveBucket || null,
        }
      );
      alert('Conversation settings saved.');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save settings.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    if (targetType === 'roles' && selectedRoles.length === 0) {
      alert('Please select at least one role');
      return;
    }

    if (targetType === 'users' && selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare the API request payload with more descriptive titles
      const baseTitle = isCritical ? '[CRITICAL] Admin Message' : 'Admin Message';
      let targetDescription = '';
      
      if (targetType === 'all') {
        targetDescription = 'to All Users';
      } else if (targetType === 'roles') {
        targetDescription = `to ${selectedRoles.join(', ')} Role(s)`;
      } else if (targetType === 'users') {
        const userNames = selectedUsers.map(userId => {
          const user = availableUsers.find(u => u.id === userId);
          return user ? user.name : `User ${userId}`;
        });
        targetDescription = `to ${userNames.join(', ')}`;
      }

      const notificationData = {
        title: `${baseTitle} ${targetDescription}`,
        message,
        type: messageType,
        delay: 0,
        priority: isCritical ? 10 : 0
      };

      if (targetType === 'all') {
        // For 'all users', we'll send to each role individually
        // First, get all available roles and send to each
        const rolePromises = availableRoles.map(role => 
          axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/scheduling/notification`,
            { ...notificationData, role }
          )
        );
        await Promise.all(rolePromises);
      } else if (targetType === 'roles') {
        // Send to selected roles
        const rolePromises = selectedRoles.map(role =>
          axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/scheduling/notification`,
            { ...notificationData, role }
          )
        );
        await Promise.all(rolePromises);
      } else if (targetType === 'users') {
        // Send to specific users
        const userPromises = selectedUsers.map(userId =>
          axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/scheduling/notification`,
            { ...notificationData, userId }
          )
        );
        await Promise.all(userPromises);
      }

      // Reset form
      setMessage('');
      setSelectedRoles([]);
      setSelectedUsers([]);
      setSelectedRoleDropdown('');
      setSelectedUserDropdown('');
      
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send message. Please try again.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-messaging">
      <h2>Send Message to Users</h2>
      
      {isLoadingData ? (
        <div className="loading-message">Loading roles and users...</div>
      ) : (
        <form onSubmit={handleSendMessage} className="messaging-form">
        <div className="form-group">
          <label htmlFor="message">Message:</label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your message here..."
            rows="4"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="messageType">Message Type:</label>
            <select
              id="messageType"
              value={messageType}
              onChange={(e) => setMessageType(e.target.value)}
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isCritical}
                onChange={(e) => setIsCritical(e.target.checked)}
              />
              Critical Message
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Send To:</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="all"
                checked={targetType === 'all'}
                onChange={(e) => setTargetType(e.target.value)}
              />
              All Users
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="roles"
                checked={targetType === 'roles'}
                onChange={(e) => setTargetType(e.target.value)}
              />
              By Role(s)
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="users"
                checked={targetType === 'users'}
                onChange={(e) => setTargetType(e.target.value)}
              />
              Specific Users
            </label>
          </div>
        </div>

        {targetType === 'roles' && (
          <div className="form-group">
            <label>Select Roles:</label>
            <div className="dropdown-selection">
              <div className="dropdown-row">
                <select
                  value={selectedRoleDropdown}
                  onChange={(e) => setSelectedRoleDropdown(e.target.value)}
                  className="role-dropdown"
                >
                  <option value="">-- Select a role --</option>
                  {availableRoles
                    .filter(role => !selectedRoles.includes(role))
                    .map(role => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddRole}
                  disabled={!selectedRoleDropdown}
                  className="add-button"
                >
                  Add Role
                </button>
              </div>
              
              {selectedRoles.length > 0 && (
                <div className="selected-items">
                  <h4>Selected Roles:</h4>
                  <div className="selected-list">
                    {selectedRoles.map(role => (
                      <div key={role} className="selected-item">
                        <span>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRole(role)}
                          className="remove-button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {availableRoles.length === 0 && (
                <p className="no-data-message">No roles available</p>
              )}
            </div>
          </div>
        )}

        {targetType === 'users' && (
          <div className="form-group">
            <label>Select Users:</label>
            <div className="dropdown-selection">
              <div className="dropdown-row">
                <select
                  value={selectedUserDropdown}
                  onChange={(e) => setSelectedUserDropdown(e.target.value)}
                  className="user-dropdown"
                >
                  <option value="">-- Select a user --</option>
                  {availableUsers
                    .filter(user => !selectedUsers.includes(user.id))
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role}) - {user.email}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddUser}
                  disabled={!selectedUserDropdown}
                  className="add-button"
                >
                  Add User
                </button>
              </div>
              
              {selectedUsers.length > 0 && (
                <div className="selected-items">
                  <h4>Selected Users:</h4>
                  <div className="selected-list">
                    {selectedUsers.map(userId => {
                      const user = availableUsers.find(u => u.id === userId);
                      return user ? (
                        <div key={userId} className="selected-item">
                          <span>{user.name} ({user.role})</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveUser(userId)}
                            className="remove-button"
                          >
                            ×
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {availableUsers.length === 0 && (
                <p className="no-data-message">No active users available</p>
              )}
            </div>
          </div>
        )}

        <button type="submit" disabled={isLoading} className="send-button">
          {isLoading ? 'Sending...' : 'Send Message'}
        </button>
      </form>
      )}

      <section className="conversation-settings-section">
        <h3>Conversation Retention</h3>
        <p className="settings-description">
          Object-linked conversations are automatically removed after the retention period.
          Enable archiving to save copies to Google Cloud Storage before deletion.
        </p>
        {isLoadingSettings ? (
          <p>Loading settings...</p>
        ) : (
          <form onSubmit={handleSaveSettings} className="settings-form">
            <div className="form-group">
              <label htmlFor="retentionDays">Retention (days):</label>
              <input
                id="retentionDays"
                type="number"
                min="1"
                max="365"
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={archiveEnabled}
                  onChange={(e) => setArchiveEnabled(e.target.checked)}
                />
                Archive to Google Cloud Storage before deletion
              </label>
            </div>
            {archiveEnabled && (
              <div className="form-group">
                <label htmlFor="archiveBucket">GCS bucket name:</label>
                <input
                  id="archiveBucket"
                  type="text"
                  value={archiveBucket}
                  onChange={(e) => setArchiveBucket(e.target.value)}
                  placeholder="bucket-name"
                />
              </div>
            )}
            <button type="submit" disabled={isSavingSettings} className="send-button">
              {isSavingSettings ? 'Saving...' : 'Save Retention Settings'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
};

export default AdminMessaging;
